import React, { useState, useEffect, useCallback } from 'react';
import { Award, BookOpen, Users, Heart, GraduationCap, Briefcase, FileText, Star, School, Globe, Mic, FlaskConical, Lightbulb, Newspaper, ExternalLink } from 'lucide-react';

export default function PrincipalWebsite() {
  const [activeSection, setActiveSection] = useState('about');

  const navItems = [
    { id: 'about', label: '个人简介' },
    { id: 'results', label: '业务成果' },
    { id: 'publications', label: '著作论文' },
    { id: 'research', label: '研究课题' },
    { id: 'influence', label: '影响力及辐射' },
    { id: 'achievements', label: '主要荣誉' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const offsets = navItems.map(item => {
        const el = document.getElementById(item.id);
        if (!el) return { id: item.id, top: Infinity };
        const rect = el.getBoundingClientRect();
        return { id: item.id, top: rect.top };
      });

      const threshold = 120;
      let current = navItems[0].id;
      for (let i = offsets.length - 1; i >= 0; i--) {
        if (offsets[i].top <= threshold) {
          current = offsets[i].id;
          break;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 56;
      const top = el.getBoundingClientRect().top + window.pageYOffset - navHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-blue-950">刘明成</h1>
          <div className="flex gap-3 text-sm overflow-x-auto">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={'#' + item.id}
                onClick={(e) => scrollToSection(e, item.id)}
                className={'whitespace-nowrap transition-colors px-2 py-1 rounded ' +
                  (activeSection === item.id
                    ? 'text-white bg-blue-800 font-semibold'
                    : 'text-gray-500 hover:text-blue-800 hover:bg-blue-50')
                }
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white">
        <div className="max-w-5xl mx-auto px-4 py-20 flex flex-col md:flex-row items-center gap-10">
          <div className="w-44 h-56 rounded-lg overflow-hidden border-4 border-white/20 shadow-lg shrink-0">
            <img
              src="/images/portrait_lmc.jpg"
              alt="刘明成校长"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-center md:text-left flex flex-col">
            <h1 className="text-4xl font-bold mb-2">刘明成</h1>
            <p className="text-xl text-blue-200 mb-1">中国教育科学研究院朝阳实验学校</p>
            <p className="text-blue-300 text-sm mb-6">北京市正高级教师 · 北京市特级校长 · 北京市数学特级教师</p>
            <p className="text-blue-100 max-w-xl leading-relaxed text-sm mb-8 text-justify">
              中国教育科学研究院朝阳实验学校校长。学术兼职包括北京师范大学教育家书院兼职研究员、中央民族大学硕士生导师、北京市基础教育学校外部评价专家组成员等。先后荣获全国科研创新型校长、北京市中学数学特级教师、北京市优秀教育工作者、北京市特级校长、山东省特级教师、山东省首届齐鲁名校长、山东省优秀教师等称号。
            </p>
            <div className="mt-auto">
              <p className="text-2xl font-bold tracking-widest text-amber-300">
                润养生命 · 润育思想 · 润泽人生
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-blue-950 mb-8 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-blue-800" />
          个人简介
        </h2>
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="text-gray-700 text-sm leading-relaxed space-y-5 flex-1">
              <p>
                刘明成，男，1964年生，数学硕士，全国科研创新校长，北京市中学正高级教师、数学特级教师、中小学特级校长、优秀教育工作者，山东省特级教师、首届齐鲁名校长、优秀教师，现任中国教育科学研究院朝阳实验学校校长。深耕基础教育领域35年，担任中小学校长26年，先后任职于山东青岛、北京朝阳两地，2012年以高端人才引进方式入京履职。
              </p>
              <p>
                二十六年校长深耕与实践历练，让他积淀了深厚的教育情怀与成熟的办学智慧，形成了独具特色的办学思想与育人体系。他始终坚守教育本心，笃信教育的本质在于唤醒生命内在自觉、激活成长原生动力，尊重每个生命的独特性，赋能孩子自主向上、向阳生长。
              </p>
              <p>
                他以<span className="font-bold text-blue-800">"润"</span>为核心办学哲学，尊重成长规律、坚守学生主体，构建温润向善、蓬勃向上的育人生态。秉持细雨润物、无声育人的教育理念，坚持以生命影响生命、以思想启迪思想，因材施教、赋能成长，力求让每个孩子各展其长、人尽其才。
              </p>
              <p>
                围绕"润"文化核心，学校确立清晰完整的办学体系：以<span className="font-bold text-blue-800">"办一所温润卓越的学校"</span>为办学愿景，以<span className="font-bold text-blue-800">"润养生命、润育思想、润泽人生"</span>为办学使命，以<span className="font-bold text-blue-800">"润品育能、筑基未来"</span>为办学理念，以<span className="font-bold text-blue-800">"润养会思考的真实行动者"</span>为育人目标。
              </p>
              <p>
                在办学实践中，他系统构建了完整的治校育人体系：推行"规范自觉、和谐活力"的润泽管理，打造"重思明辨、灵动生长"的思考力课堂，构建"贵品渊知、卓能强体"的润泽课程，培育"教研相长、深思力行"的润泽教师队伍，着力培养"会思考、有情怀、敢担当、能作为"的新时代润泽少年，办学成效显著、成果丰硕。
              </p>
              <p>
                个人学术积淀深厚，兼任北京师范大学教育家书院兼职研究员、中央民族大学硕士生导师等社会学术职务。深耕教研、笔耕不辍，先后出版专著3部、主编论著5部，在省市级及核心期刊发表教育论文30余篇；荣获国家级教学成果奖1项、北京市教学成果奖、课程成果奖等多项重磅荣誉，以扎实的专业能力与先进的办学理念，持续赋能学校高质量发展。
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-center">
              <div className="w-44 h-56 rounded-lg overflow-hidden shadow-md border border-blue-100">
                <img
                  src="/images/jiaoyujia_lmc.webp"
                  alt="刘明成——教育家风采"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center italic">《当代教育家》封面人物</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-blue-900 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { num: '35', label: '教育年限' },
            { num: '26', label: '校长任期（年）' },
            { num: '3', label: '所薄弱学校变强校' },
            { num: '20+', label: '项市级以上荣誉' },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-3xl font-bold">{s.num}</div>
              <div className="text-blue-300 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Business Results Section */}
      <section id="results" className="bg-blue-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-950 mb-8 flex items-center gap-2">
            <Award className="w-6 h-6 text-blue-800" />
            业务成果
          </h2>
          <div className="bg-white rounded-xl shadow-sm p-8 space-y-4">
            <h3 className="font-semibold text-gray-800 mb-4">教学成果奖</h3>
            {[
              { year: '2023.4', text: '教学成果《思考力课堂的理论创建与实践创新》获国家基础教育教学成果二等奖（成果主持人）' },
              { year: '2023.12', text: '教学成果《润养会思想的真实行动者——学校过程性德育的探索与实践》获朝阳区第十九届教学成果年度奖（成果主持人）' },
              { year: '2022.10', text: '教学成果《润泽教育理念下的立德树人育人体系的构建与实践》获朝阳区第十八届教学成果年度奖（成果主持人）' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 text-sm text-gray-700 border-l-2 border-blue-200 pl-4 py-1">
                <span className="text-blue-800 font-medium whitespace-nowrap">{item.year}</span>
                <span>{item.text}</span>
              </div>
            ))}

            <h3 className="font-semibold text-gray-800 mb-4 mt-8">课程成果奖</h3>
            {[
              { year: '2021-2022', text: '课程成果《立德敦品弘毅励行——九年一贯制学校"润泽德育"课程体系的构建与实践》获北京市基础教育课程建设成果一等奖（成果主持人）' },
              { year: '2022-2023', text: '课程成果《双减背景下以"组"为单位开展作业整体设计的实践研究》获北京市基础教育课程建设成果一等奖（第二作者）' },
              { year: '2019-2020', text: '课程成果《润泽课程的整体建构与深度实施》获北京市基础教育课程建设成果二等奖（成果主持人）' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 text-sm text-gray-700 border-l-2 border-blue-200 pl-4 py-1">
                <span className="text-blue-800 font-medium whitespace-nowrap">{item.year}</span>
                <span>{item.text}</span>
              </div>
            ))}

            <h3 className="font-semibold text-gray-800 mb-4 mt-8">获奖论文</h3>
            {[
              { year: '2021-2022', text: '论文《指向提质减负的思考力课堂的创新性构建与实施》获北京市基础教育论文评选一等奖（第一作者）' },
              { year: '2021-2022', text: '论文《双减背景下基于以年级组为单位统筹作业管理的实践研究》获北京市基础教育论文评选一等奖（第二作者）' },
              { year: '2020.9', text: '论文《基于润泽教师培养的内生式校本培训实践》获"十三五"时期北京市基础教育教师培训优秀科研成果一等奖（第一作者）' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 text-sm text-gray-700 border-l-2 border-blue-200 pl-4 py-1">
                <span className="text-blue-800 font-medium whitespace-nowrap">{item.year}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications Section */}
      <section id="publications" className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-blue-950 mb-8 flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-800" />
          著作论文
        </h2>
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-6">
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">专著</h3>
            <div className="space-y-3">
              {[
                '《润泽教育：化育生命的校本实践与研究》（复旦大学出版社 2022.1）',
                '《润泽文化的力量》（山东大学出版社 2021.6）',
                '《思者常明行者常成》（北京出版社 2017.11）',
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <FileText className="w-4 h-4 mt-0.5 text-blue-800 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-800 mb-4">主编论著及教材</h3>
            <div className="space-y-3">
              {[
                '主编论著《润泽德育的校本探索与实践——润思泽行的生命对话》（天津出版传媒集团 2022.3）',
                '主编论著《用课程涵养生命——润泽教育校本课程的建构及实践》（天津出版传媒集团 2023.3）',
                '主编论著《让成长看得见——思考力课堂的建构及实践》（天津出版传媒集团 2022.3）',
                '主编论著《成长心语》（海豚出版社 2020.11）',
                '主编校本教材《新智人新时代》（中国工信出版社 2021.8）',
                '主编校本教材《北斗导航智慧生活》（中国书籍出版社 2017.7）',
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <FileText className="w-4 h-4 mt-0.5 text-blue-800 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-800 mb-4">核心期刊发表论文</h3>
            <div className="space-y-3">
              {[
                '《文化内生：现代学校治理的有效着力点》（《人民教育》2020年第7期）',
                '《"三立并举"激发学校干部队伍活力》（《人民教育》2020年第22期）',
                '《走向深度学习："思考力课堂"的构建与实施策略》（《中小学管理》2019年第12期）',
                '《动静相宜：对一贯制学校教师评价的基本思考》（《中小学管理》2020年第7期）',
                '《指向深度思考力培养的课堂教学评价》（《基础教育课程》2020年第5期）',
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <FileText className="w-4 h-4 mt-0.5 text-blue-800 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-800 mb-4">其他重要期刊论文</h3>
            <div className="space-y-3">
              {[
                '《落实立德树人根本任务的校本化实践》（《中小学校长》2022年第2期）',
                '《九年一贯制学校德育课程一体化的实践探索》（《中国德育》2022年第22期）',
                '《润泽德育的实践探索》（《中国德育》2021年第9期）',
                '《凸显学校治理现代化中心与路径的校本实践》（《中国教师》2020年第11期）',
                '《润泽德育课程一体化的构建与运行机制研究》（《中国教师》2024年第1期）',
                '《彰显过程的力量：中小学过程性德育体系探索》（《中国基础教育》2024年第8期）',
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <FileText className="w-4 h-4 mt-0.5 text-blue-800 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Research Projects */}
      <section id="research" className="bg-blue-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-950 mb-8 flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-blue-800" />
            研究课题
          </h2>
          <div className="bg-white rounded-xl shadow-sm p-8 space-y-4">
            {[
              { year: '2022年结题', text: '中国教科院重点课题"九年一贯制学校治理创新的行动研究"（主持人）' },
              { year: '2021.6结题', text: '北京市教育科学"十三五"规划课题"初中课堂开展深度学习的理论与实践探究"（主持人）' },
              { year: '2020.7结题', text: '北京市教育学会"十三五"教育科研课题"U-S合作基础上初中心理健康教育课程建设研究"（主持人）' },
              { year: '2020.1结题', text: '全国教育规划课题"学区化管理背景下中小学协同发展机制研究"（参与者）' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 text-sm text-gray-700 bg-blue-50 rounded-lg p-4">
                <FlaskConical className="w-4 h-4 mt-0.5 text-blue-800 shrink-0" />
                <div>
                  <span className="text-blue-800 font-medium">{item.year}</span>
                  <span className="mx-2">|</span>
                  <span>{item.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Influence Section */}
      <section id="influence" className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-blue-950 mb-8 flex items-center gap-2">
          <Globe className="w-6 h-6 text-blue-800" />
          影响力及辐射
        </h2>
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-6">
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">学术聘任</h3>
            <div className="space-y-3">
              {[
                { year: '2021.8', text: '被聘为国家教育行政学院骨干校长培训班实践导师' },
                { year: '2021.11', text: '被聘为曲阜师范大学兼职教授' },
                { year: '2018.4', text: '被评为中国教育学会管理分会委员' },
                { year: '2016.10', text: '被聘为中央民族大学硕士生导师' },
                { year: '2015.12', text: '被聘为北师大教育家书院兼职研究员' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm text-gray-700 border-l-2 border-blue-200 pl-4">
                  <span className="text-blue-800 font-medium whitespace-nowrap">{item.year}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-800 mb-4">学术报告与交流</h3>
            <div className="space-y-3">
              {[
                { year: '2023.12', text: '在北京市德育学会年会上作报告《润泽德育体系一体化的构建与实践》', linkType: 'none' },
                { year: '2022.10', text: '在北京市教科院举办的"让核心素养落地为学生成长赋能"研讨会上主题发言《润泽教育理念下立德树人育人体系的构建与实践》', linkType: 'none' },
                { year: '2022.10', prefix: '北京市政府网站刊登', linkText: '"对话·我和首都教育十年——我的十年故事"', link: 'https://xinwen.bjd.com.cn/content/s6346599ce4b018021744dfdb.html', linkType: 'partial' },
                { year: '2022.10', text: '在《中国德育》举办的明德讲堂：大思政课建设研究作报告"润泽德育课程一体化建设"', linkType: 'none' },
                { year: '2022.3', text: '在北京市基础教育教研中心举办的双减背景下提质减负策略现场会上做交流发言《思考力课堂：双减背景下提质减负的一条有效途径》', linkType: 'none' },
                { year: '2021.12', prefix: '在《北京日报》刊登文章', linkText: '"双特谈双减：建设高质量的育人体系"', link: 'https://baijiahao.baidu.com/s?id=1718543751759887052&wfr=spider&for=pc', linkType: 'partial' },
                { year: '2021.6', text: '在北京市教育学会举办中小学综合实践活动特色学校课程观摩研讨会上做报告《让综合实践课程更具有生命力》', linkType: 'none' },
                { year: '2021.12', text: '讲座《中小学教师评价》入选国家教育行政学院课程资源库，用于全国教育干部、教师网络培训学习', linkType: 'none' },
                { year: '2020.10', text: '讲座《基于学校优质发展的变革策略》入选国家教育行政学院课程资源库，用于全国教育干部、教师网络培训学习', linkType: 'none' },
                { year: '2019.12', text: '在中国教科院举办的综合改革试验区第七届高质量课堂展示活动中作为评委组长做评课报告', linkType: 'none' },
                { year: '2018.11', text: '在中国教科院举办的首届全国新样态学校论坛活动中做交流《建设魅力课堂从明晰教学目标开始》', linkType: 'none' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm text-gray-700 border-l-2 border-blue-200 pl-4 py-1">
                  <span className="text-blue-800 font-medium whitespace-nowrap">{item.year}</span>
                  {item.linkType === 'partial' ? (
                    <span>
                      {item.prefix}
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:text-blue-900 underline inline-flex items-center gap-0.5"
                      >
                        {item.linkText}
                        <ExternalLink className="w-3 h-3 shrink-0 inline" />
                      </a>
                    </span>
                  ) : (
                    <span>{item.text}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-800 mb-4">媒体报道（部分）</h3>
            <div className="space-y-3">
              {[
                {
                  text: '封面人物丨神奇盘活三所薄弱校，这位校长的治校秘籍令人惊叹！（当代教育家）',
                  link: 'https://mp.weixin.qq.com/s?__biz=MzU1NzUxODgyNA==&mid=2247579710&idx=1&sn=2b7c3c0afa2463fadc7dd6a58af7daf6&chksm=fdf6660a72f664f198fda78e585a5012171c447ee36f3578fcf07a4f538c78587994173a9773&scene=27',
                },
                {
                  text: '文化建设这盘棋，中小学该怎么下——北京市朝阳区学校文化建设观察（中国教育报）',
                  link: 'https://www.enaea.edu.cn/case/jcjy/2020/88716.html',
                },
                {
                  text: '如何激发中小学办学活力？半月谈第2期"教育改革对话会"在京举办（中共中央宣传部委托新华通讯社主办半月谈）',
                  link: 'http://www.banyuetan.org/jy/detail/20210425/1000200033136041619333571328340718_1.html',
                },
                {
                  text: '中国教育科学研究院朝阳实验学校校长刘明成：润泽教育——会思想 有情怀 敢行动（中国教育网络电视台）',
                  link: 'https://www.centv.cn/p/368169.html',
                },
                {
                  text: '新时代学校"高质量课堂"暨中国教科院朝阳实验学校"思考力课堂"建设研讨会圆满举行（教育头条）',
                  link: 'http://www.edutoutiao.com/video/10325.html',
                },
                {
                  text: '刘明成："润泽德育"课程一体化构建与运行机制研究（中国教师）',
                  link: 'https://mp.weixin.qq.com/s?__biz=MzkyMTI1MDI4MA==&mid=2247535740&idx=1&sn=d15f2285c4f0500877d9773b6c8b488b&source=41&poc_token=HJQeJGqjwiK5wK_4SGkI-jiGdLR8ESULN9je4erg',
                },
                {
                  text: '中国德育·明德讲堂第16讲："大思政课"建设研究（中国德育）',
                  link: 'https://mp.weixin.qq.com/s?__biz=MzA4NzE4OTcyMg==&mid=2655828889&idx=2&sn=f728b6ba3ad3a0a0f6819ef7df7a3ce5&chksm=8b852a2cbcf2a33a61cbb2fdad553b5fbfeb0c9602e14adfa918be90a354d8b323af1822cb3e&scene=27',
                },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm text-gray-700 border-l-2 border-blue-200 pl-4 py-1">
                  <Newspaper className="w-4 h-4 mt-0.5 text-blue-800 shrink-0" />
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 underline flex items-center gap-1"
                  >
                    {item.text}
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Honors Section */}
      <section id="achievements" className="bg-blue-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-950 mb-8 flex items-center gap-2">
            <Award className="w-6 h-6 text-blue-800" />
            主要荣誉
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: '北京市中小学特级校长', org: '北京市教委', year: '2023.1' },
              { title: '北京市优秀教育工作者', org: '北京市教委', year: '2022.9' },
              { title: '北京市特级教师', org: '北京市教委', year: '2015.3' },
              { title: '山东省特级教师', org: '山东省政府', year: '2010.10' },
              { title: '山东省首届齐鲁名校长工程人选', org: '山东省教委', year: '2009.8' },
              { title: '全国科研创新型校长', org: '中国教育学会', year: '2007.10' },
              { title: '山东省优秀教师', org: '山东省教育厅、人事厅', year: '1995.9' },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow border border-blue-100"
              >
                <div className="bg-blue-100 text-blue-800 rounded-lg p-2">
                  <Award className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-sm">{item.title}</h3>
                  <p className="text-gray-500 text-xs">{item.org}</p>
                </div>
                <span className="text-xs text-blue-700 whitespace-nowrap">{item.year}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 text-blue-200 py-8 text-center text-sm">
        <p>© 2023 刘明成</p>
        <p className="mt-2 text-base font-bold tracking-widest text-amber-300">
          润养生命 · 润育思想 · 润泽人生
        </p>
      </footer>
    </div>
  );
}
